import React, { useCallback, useMemo, useRef, useState } from 'react';

import cx from 'classnames';
import { Button } from 'components';
import { useTranslation } from 'react-i18next';

import { VisitorType } from 'lane-shared/domains/visitorManagement/types/VisitorType';
import { useVisitorInviteForm } from 'lane-shared/hooks';
import useFlag from 'lane-shared/hooks/useFlag';
import { VisitorManagementFeatureProperties } from 'application-types';
import { FeatureFlag } from 'constants-flags';
import { useVisitorWatchlistLookup } from 'react-hooks';
import { VisitorInviteCustomFieldValues } from 'domains/visitorManagement/components/VisitorInviteCustomFieldValues/VisitorInviteCustomFieldValues';
import { CustomFieldsSection } from 'domains/visitorManagement/feature/components/CustomFieldsSection/CustomFieldsSection';
import { buildVisitorCustomFieldValuesFromFormState } from 'domains/visitorManagement/helpers/customFieldValuesFormState';
import { selectEnabledCustomFieldsForInvite } from 'domains/visitorManagement/helpers/selectEnabledCustomFieldsForInvite';
import { validateCustomFieldsForForm } from 'domains/visitorManagement/validators/validateCustomFieldValue';
import type { VisitorCustomFieldConfig } from 'graphql-query-contracts';

import VisitorInput from '../VisitorInput/VisitorInput';
import VisitorPhoneInput from '../VisitorPhoneInput/VisitorPhoneInput';

import styles from './VisitorInviteForm.scss';

const EMPTY_CUSTOM_FIELD_IDS: string[] = [];

function InviteForm({
  className,
  properties,
  value,
  defaultValue,
  isEditMode = false,
  visitorToEdit,
  onAdd,
  onEditSave,
  onFieldChange,
  isPreview = false,
  tenantId,
  customFieldConfigs: customFieldConfigsProp,
}: {
  className?: string;
  properties: VisitorManagementFeatureProperties;
  value?: VisitorType[];
  defaultValue?: VisitorType;
  isEditMode?: boolean;
  visitorToEdit?: VisitorType;
  onAdd?: (visitor: VisitorType) => void;
  onEditSave?: (visitor: VisitorType) => void;
  onFieldChange?: (changed: boolean) => void;
  isPreview?: boolean;
  tenantId?: string;
  customFieldConfigs?: VisitorCustomFieldConfig[];
}): React.ReactElement {
  const { t } = useTranslation();
  const { performLookup } = useVisitorWatchlistLookup();
  const visitorManagementCustomFieldsEnabled = useFlag(
    FeatureFlag.VisitorManagementCustomFields,
    false
  );
  const [customFieldValuesByConfigId, setCustomFieldValuesByConfigId] = useState<
    Record<string, string | string[]>
  >({});
  const customFieldValuesByConfigIdRef = useRef(customFieldValuesByConfigId);

  const customFieldConfigIds = useMemo(
    () => properties.customFieldIds ?? EMPTY_CUSTOM_FIELD_IDS,
    [properties.customFieldIds]
  );

  const [configsFromSection, setConfigsFromSection] = useState<VisitorCustomFieldConfig[]>([]);

  const customFieldConfigs = customFieldConfigsProp?.length
    ? customFieldConfigsProp
    : configsFromSection;

  const inviteCustomFieldsToShow = useMemo(
    () =>
      visitorManagementCustomFieldsEnabled
        ? selectEnabledCustomFieldsForInvite(customFieldConfigs, customFieldConfigIds)
        : [],
    [visitorManagementCustomFieldsEnabled, customFieldConfigs, customFieldConfigIds]
  );

  const handleCustomFieldValueChange = useCallback(
    (configId: string, fieldKey: string, fieldValue: string | string[]) => {
      const next = { ...customFieldValuesByConfigIdRef.current, [configId]: fieldValue };

      customFieldValuesByConfigIdRef.current = next;
      setCustomFieldValuesByConfigId(next);
      setCustomFieldErrors(prev => {
        const updated = { ...prev };
        delete updated[fieldKey];
        return updated;
      });
    },
    []
  );

  const buildCustomFieldValuesForVisitor = useCallback((): VisitorType['customFieldValues'] => {
    if (!visitorManagementCustomFieldsEnabled) {
      return undefined;
    }

    const values = buildVisitorCustomFieldValuesFromFormState(
      customFieldValuesByConfigIdRef.current,
      customFieldConfigIds
    );

    return values.length > 0 ? values : undefined;
  }, [
    visitorManagementCustomFieldsEnabled,
    customFieldConfigIds,
  ]);

  const customFieldConfigsForForm = useMemo(() => {
    if (!customFieldConfigIds.length || !customFieldConfigs?.length) return [];
    const set = new Set(customFieldConfigIds);
    return customFieldConfigs
      .filter((c) => set.has(c.id) && c.isEnabled)
      .map((c) => ({ ...c, options: c.options?.map((o) => (typeof o === 'string' ? o : o.value)) ?? null }));
  }, [customFieldConfigs, customFieldConfigIds]);

  const [customFieldErrors, setCustomFieldErrors] = useState<Record<string, string>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const handleAddSuccess = useCallback(
    (visitor: VisitorType) => {
      const cfv = buildCustomFieldValuesForVisitor();

      onAdd?.(cfv?.length ? { ...visitor, customFieldValues: cfv } : visitor);
      customFieldValuesByConfigIdRef.current = {};
      setCustomFieldValuesByConfigId({});
      setCustomFieldErrors({});
      setSubmitAttempted(false);
    },
    [buildCustomFieldValuesForVisitor, onAdd]
  );

  const {
    email,
    setEmail,
    emailValidator,
    emailRequiredValidator,
    firstName,
    setFirstName,
    firstNameValidator,
    lastName,
    setLastName,
    lastNameValidator,
    company,
    setCompany,
    companyValidator,
    phone,
    setPhone,
    phoneValidator,
    phoneRequiredValidator,
    hasChanged,
    addVisitor,
    resetFields,
    isRequired,
    isHidden,
    updateVisitor,
    isPristine,
  } = useVisitorInviteForm({
    defaultValue,
    properties,
    translator: t,
    isEditMode,
    visitorToEdit,
    onAddSuccess: handleAddSuccess,
    onEditSuccess: onEditSave,
    tenantId,
    onLookupWatchlist: performLookup,
  });

  if (onFieldChange) {
    onFieldChange(isPristine);
  }

  const showStandardFieldErrors = hasChanged || submitAttempted;

  const showCustomFieldValues =
    visitorManagementCustomFieldsEnabled &&
    inviteCustomFieldsToShow.length > 0 &&
    !isPreview;

  const handleOnClick = useCallback(async () => {
    if (showCustomFieldValues && customFieldConfigsForForm.length > 0) {
      const requiredMessage = t(
        'web.content.feature.visitorManagement.visitorInvite.form.requiredField'
      );
      const currentValues = buildVisitorCustomFieldValuesFromFormState(
        customFieldValuesByConfigIdRef.current,
        customFieldConfigIds
      ).map((v) => ({ customFieldConfigId: v.customFieldConfigId, value: v.value.join(',') }));
      const errors = validateCustomFieldsForForm(
        customFieldConfigsForForm,
        currentValues,
        { requiredMessage }
      );

      if (Object.keys(errors).length > 0) {
        setSubmitAttempted(true);
        setCustomFieldErrors(errors);
        return;
      }

      setCustomFieldErrors({});
    }

    if (isEditMode) {
      updateVisitor();

      return;
    }

    await addVisitor();
  }, [
    showCustomFieldValues,
    customFieldConfigsForForm,
    customFieldConfigIds,
    t,
    isEditMode,
    updateVisitor,
    addVisitor,
  ]);

  const handleClear = useCallback(() => {
    customFieldValuesByConfigIdRef.current = {};
    setCustomFieldValuesByConfigId({});
    setCustomFieldErrors({});
    setSubmitAttempted(false);
    resetFields();
  }, [resetFields]);

  const addButtonText = useMemo(() => {
    if (isEditMode)
      return 'web.content.feature.visitorManagement.visitorInvite.form.save';

    return value?.length
      ? 'web.content.feature.visitorManagement.visitorInvite.form.addAnother'
      : 'web.content.feature.visitorManagement.visitorInvite.form.add';
  }, [value?.length, isEditMode]);

  return (
    <div className={cx(styles.visitorInviteForm, className)}>
      <div>
        <VisitorInput
          testId="email"
          displayName={t(
            'web.content.feature.visitorManagement.visitorInvite.form.email'
          )}
          value={email}
          placeholder={t(
            'web.content.feature.visitorManagement.visitorInvite.form.placeholder.email'
          )}
          type="email"
          onChange={email => setEmail(email)}
          validation={
            isRequired(properties.emailSetting)
              ? emailRequiredValidator
              : emailValidator
          }
          isPristine={!showStandardFieldErrors}
          isHidden={isHidden(properties.emailSetting)}
          isRequired={isRequired(properties.emailSetting)}
          isDisabled={isPreview}
        />
        <VisitorInput
          testId="firstname"
          displayName={t(
            'shared.content.feature.visitorManagement.visitorInvite.form.firstName'
          )}
          value={firstName}
          placeholder={t(
            'shared.content.feature.visitorManagement.visitorInvite.form.firstName'
          )}
          onChange={firstName => setFirstName(firstName)}
          validation={firstNameValidator}
          isPristine={!showStandardFieldErrors}
          isRequired={isRequired(properties.nameSetting)}
          isDisabled={isPreview}
        />
        <VisitorInput
          testId="lastname"
          displayName={t(
            'shared.content.feature.visitorManagement.visitorInvite.form.lastName'
          )}
          value={lastName}
          placeholder={t(
            'shared.content.feature.visitorManagement.visitorInvite.form.lastName'
          )}
          onChange={lastName => setLastName(lastName)}
          validation={lastNameValidator}
          isPristine={!showStandardFieldErrors}
          isRequired={isRequired(properties.nameSetting)}
          isDisabled={isPreview}
        />
        <VisitorPhoneInput
          testId="phone"
          displayName={t(
            'shared.content.feature.visitorManagement.visitorInvite.form.cellNumber'
          )}
          value={phone}
          placeholder={t(
            'shared.content.feature.visitorManagement.visitorInvite.form.cellNumber'
          )}
          onChange={phone => setPhone(phone)}
          validation={
            isRequired(properties.phoneSetting)
              ? phoneRequiredValidator
              : phoneValidator
          }
          isPristine={!showStandardFieldErrors}
          isHidden={isHidden(properties.phoneSetting)}
          isRequired={isRequired(properties.phoneSetting)}
          isDisabled={isPreview}
        />
        <VisitorInput
          testId="company"
          displayName={t(
            'shared.content.feature.visitorManagement.visitorInvite.form.company'
          )}
          value={company}
          placeholder={t(
            'shared.content.feature.visitorManagement.visitorInvite.form.company'
          )}
          onChange={company => setCompany(company)}
          validation={
            isRequired(properties.companySetting) ? companyValidator : undefined
          }
          isPristine={!showStandardFieldErrors}
          isHidden={isHidden(properties.companySetting)}
          isRequired={isRequired(properties.companySetting)}
          isDisabled={isPreview}
        />
      </div>
      <VisitorInviteCustomFieldValues
        isPreview={isPreview}
        valueByConfigId={customFieldValuesByConfigId}
        onFieldValueChange={handleCustomFieldValueChange}
        fieldsToShow={inviteCustomFieldsToShow}
        errors={showCustomFieldValues ? customFieldErrors : undefined}
      />
      <CustomFieldsSection isPreview={isPreview} hideReadOnlyCustomFields onConfigsLoaded={setConfigsFromSection} />
      <div className={styles.buttonContainer}>
        <Button
          testId="addVisitorButton"
          variant="contained"
          onClick={handleOnClick}
          size="small"
          disabled={isPreview}
        >
          {t(addButtonText)}
        </Button>
        <Button
          className={styles.clearButton}
          testId="clearButton"
          variant="text"
          onClick={handleClear}
          size="small"
          disabled={isPreview}
        >
          {t('web.content.feature.visitorManagement.visitorInvite.form.clear')}
        </Button>
      </div>
    </div>
  );
}

export default InviteForm;
